import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Edit, Add, ChevronRight } from '@mui/icons-material';
import { useAuth } from 'AuthContext';
import useClubContext from 'contexts/Club';
import useGoalsContext from 'contexts/Goals';
import { getGoals } from 'services/goals/goals.service';
import GoalFormModal from 'components/Goals/GoalFormModal';
import QuickGoalCompletion from 'components/QuickGoalCompletion';
import GoalDetailsModal from 'components/Goals/GoalDetailsModal';
import PausedGoalChip from 'components/PausedGoalChip';
import ClubGoalChip from 'components/ClubGoalChip';
import PersonalGoalsReport from 'components/PersonalGoalsReport';
import ClubGoalsReport from 'components/ClubGoalsReport';
import ClubGoalsList from 'components/ClubGoalsList';
import Layout from 'components/Layout';
import GoalsTour from 'components/Tours/GoalsTour';
import {
  getGoalTypeLabel,
  getGoalTypeColor,
  getProgressInfo,
  getProgressBarValue,
  isClubLinkedGoal,
} from 'utils/goalHelpers';
import { aprilFoolsGoalTitle } from 'utils/aprilFools2026';

const CLUB_GOALS_TAB_INDEX = 1;
const REPORTS_TAB_INDEX = 2;

const Goals = () => {
  const { user } = useAuth();
  const { currentClub } = useClubContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { goals, loading, error, addGoal, updateGoal, deleteGoal, refreshGoals } = useGoalsContext();
  const [localError, setLocalError] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [retiredGoals, setRetiredGoals] = useState([]);
  const [retiredLoading, setRetiredLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  /** 0 = personal report, 1 = club report (only used when main tab is Reports) */
  const [reportsSubTab, setReportsSubTab] = useState(0);

  const isClubTabFromUrl = location.pathname === '/goals/club';
  const effectiveTab = isClubTabFromUrl ? CLUB_GOALS_TAB_INDEX : currentTab;

  useEffect(() => {
    if (isClubTabFromUrl && currentTab !== CLUB_GOALS_TAB_INDEX) {
      setCurrentTab(CLUB_GOALS_TAB_INDEX);
    }
  }, [isClubTabFromUrl, currentTab]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const report = searchParams.get('report');
    if (tab === 'reports') {
      setCurrentTab(REPORTS_TAB_INDEX);
      if (report === 'club') {
        setReportsSubTab(1);
      } else if (report === 'personal') {
        setReportsSubTab(0);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (!showRetired || !user?.uid || !currentClub?.id) {
      setRetiredGoals([]);
      return undefined;
    }

    let cancelled = false;
    setRetiredLoading(true);
    getGoals(user.uid, currentClub.id, { archived: true })
      .then((data) => {
        if (!cancelled) setRetiredGoals(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Failed to fetch retired goals:', err);
        if (!cancelled) setRetiredGoals([]);
      })
      .finally(() => {
        if (!cancelled) setRetiredLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showRetired, user?.uid, currentClub?.id, goals]);

  const handleTabChange = (e, newValue) => {
    setCurrentTab(newValue);
    if (newValue === CLUB_GOALS_TAB_INDEX) {
      navigate('/goals/club', { replace: true });
    } else {
      navigate('/goals', { replace: true });
    }
  };

  const handleGoalClick = (goal) => {
    setSelectedGoal(goal);
    setDetailsModalOpen(true);
  };

  const handleCreateGoal = () => {
    setEditingGoal(null);
    setModalOpen(true);
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setModalOpen(true);
  };

  const handleSaveGoal = async (goalData) => {
    if (!user) return;

    try {
      if (editingGoal) {
        await updateGoal(editingGoal.id, goalData);
      } else {
        await addGoal(goalData);
      }
      setModalOpen(false);
    } catch (err) {
      setLocalError('Failed to save goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!user) return;

    try {
      await deleteGoal(goalId);
    } catch (err) {
      setLocalError('Failed to delete goal');
    }
  };



  // Calculate consistency rate for a habit goal based on current period
  const calculateHabitConsistencyRate = (goal) => {
    if (goal.type !== 'habit' || !goal.cadence || !goal.progress) {
      return null;
    }
    
    // Use progress data if available
    // For habits, progress.completed indicates current period completion
    // We'll use a simple approach: if completed, 100%, otherwise use progress percentage
    if (goal.progress.completed) {
      return 100;
    }
    
    // If we have actual/target, calculate percentage
    if (goal.progress.actual !== undefined && goal.progress.target !== undefined && goal.progress.target > 0) {
      return (goal.progress.actual / goal.progress.target) * 100;
    }
    
    // Fallback: use 0 if no progress data
    return 0;
  };

  const activeGoals = goals
    .filter((goal) => {
    if (goal.archived) return false;
    if (showCompleted) return true;
    return !goal.completed;
  });

  const filteredGoals = [...activeGoals, ...(showRetired ? retiredGoals : [])].sort((a, b) => {
    // Sort habits by consistency rate (auto-calculated), other goals by creation date
    if (a.type === 'habit' && b.type === 'habit') {
      const consistencyA = calculateHabitConsistencyRate(a) || 0;
      const consistencyB = calculateHabitConsistencyRate(b) || 0;
      
      if (consistencyB !== consistencyA) {
        return consistencyB - consistencyA; // Descending
      }
      
      // Tie-breaking: sort by creation date (oldest first)
      const dateA = new Date(a.created_at || a.createdAt || 0);
      const dateB = new Date(b.created_at || b.createdAt || 0);
      return dateA - dateB;
    }
    // For non-habits or mixed types, keep original order
    return 0;
  });


  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout onRefresh={refreshGoals}>
      <GoalsTour />
      <Box
        sx={{
          p: 3,
          overflowX: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Goals</Typography>
          {effectiveTab === 0 && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateGoal}
              data-tour="goals-create"
            >
              Create goal
            </Button>
          )}
        </Box>

        <Tabs
          value={effectiveTab}
          onChange={handleTabChange}
          sx={{ mb: 3 }}
          data-tour="goals-tabs"
        >
          <Tab label="My goals" />
          <Tab label="Club goals" />
          <Tab label="Reports" />
        </Tabs>

        {effectiveTab === REPORTS_TAB_INDEX && (
          <Box data-tour="goals-reports">
            <ToggleButtonGroup
              exclusive
              value={reportsSubTab}
              onChange={(_, v) => v !== null && setReportsSubTab(v)}
              aria-label="Report scope"
              size="small"
              color="primary"
              sx={{
                mb: 2,
                '& .MuiToggleButton-root': {
                  px: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                },
              }}
            >
              <ToggleButton value={0}>Personal</ToggleButton>
              <ToggleButton value={1}>Club</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ pt: 0.5 }}>
              {reportsSubTab === 0 && <PersonalGoalsReport />}
              {reportsSubTab === 1 && <ClubGoalsReport />}
            </Box>
          </Box>
        )}

        {effectiveTab === CLUB_GOALS_TAB_INDEX && <ClubGoalsList />}

        {effectiveTab === 0 && (
          <>

        <Box sx={{ mb: 3 }} data-tour="goals-quick">
          <QuickGoalCompletion />
        </Box>

        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
              />
            }
            label="Show completed goals"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showRetired}
                onChange={(e) => setShowRetired(e.target.checked)}
              />
            }
            label="Show retired goals"
          />
        </Box>

        {(error || localError) && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
            {error || localError}
          </Alert>
        )}

        <TableContainer component={Paper} data-tour="goals-table">
          <Table>
            <TableHead data-tour="goals-table-header">
              <TableRow>
                <TableCell width={50}></TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {retiredLoading && showRetired && filteredGoals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredGoals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      {showRetired && showCompleted
                        ? 'No goals to show'
                        : showRetired
                          ? 'No retired goals'
                          : showCompleted
                            ? 'No completed goals'
                            : 'No active goals. Create your first goal!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredGoals.map((goal) => {
                  const progress = goal.progress;
                  const hasProgress = progress && (goal.type === 'habit' || goal.type === 'metric');
                  const isRetired = Boolean(goal.archived);

                  return (
                    <TableRow 
                      key={goal.id} 
                      sx={{ 
                        opacity: goal.completed || isRetired ? 0.6 : 1,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={() => handleGoalClick(goal)}
                    >
                      <TableCell>
                        <ChevronRight sx={{ color: 'text.secondary' }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1" sx={{ fontWeight: goal.completed ? 'normal' : 'bold' }}>
                          {aprilFoolsGoalTitle(goal.title, goal.id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                          <Chip
                            label={getGoalTypeLabel(goal)}
                            color={getGoalTypeColor(goal.type)}
                            size="small"
                          />
                          {isClubLinkedGoal(goal) && <ClubGoalChip />}
                          {goal.isPaused && <PausedGoalChip />}
                          {isRetired && (
                            <Chip label="Retired" size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getProgressInfo(goal)}
                        </Typography>
                        {hasProgress && (
                          <LinearProgress
                            variant="determinate"
                            value={getProgressBarValue(goal)}
                            sx={{ height: 4, borderRadius: 1, mt: 0.5 }}
                            color={progress.completed ? 'success' : 'primary'}
                          />
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {!isRetired && (
                          <IconButton
                            onClick={() => handleEditGoal(goal)}
                            size="small"
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <GoalFormModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveGoal}
          onArchive={handleDeleteGoal}
          editingGoal={editingGoal}
        />

        <GoalDetailsModal
          open={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedGoal(null);
          }}
          goal={selectedGoal}
        />
          </>
        )}
      </Box>
    </Layout>
  );
};

export default Goals;
